import firebase from "firebase/compat/app";

export interface PaymentSetupData {
  paymentMethod?: "customUrl" | "giveSpark";
  customDonationUrl?: string;
  stripeConnected?: boolean;
  progressTracker?: string;
  setupDate?: string;
}

export const usesExternalGivingPage = (
  paymentSetup: PaymentSetupData | null | undefined,
  customDonationUrl?: string | null
): boolean =>
  paymentSetup?.paymentMethod === "customUrl" || !!customDonationUrl?.trim();

export const loadPaymentSetupData = async (charityId: string): Promise<PaymentSetupData | null> => {
  try {
    const database = firebase.database();
    const paymentSetupRef = database.ref(`${charityId}/charitySetup/payment`);
    const snapshot = await paymentSetupRef.once('value');
    const data = snapshot.val();

    return data || null;
  } catch (error) {
    console.error("Error loading payment setup data:", error);
    throw error;
  }
};

export const savePaymentSetupData = async (
  charityId: string,
  data: Partial<PaymentSetupData>
): Promise<void> => {
  try {
    const database = firebase.database();
    const paymentSetupRef = database.ref(`${charityId}/charitySetup/payment`);

    const dataWithTimestamp = {
      ...data,
      lastUpdated: firebase.database.ServerValue.TIMESTAMP,
    };

    await paymentSetupRef.update(dataWithTimestamp);
  } catch (error) {
    console.error("Error saving payment setup data:", error);
    throw error;
  }
};

export const determineStartingStep = (data: PaymentSetupData | null): number => {
  if (!data || !data.paymentMethod) {
    return 0;
  }

  if (data.paymentMethod === "customUrl" || data.paymentMethod === "giveSpark") {
    return 1;
  }

  return 0;
};

export interface StripeConfig {
  accountType: string;
  stripeClientId: string;
  redirectUrl: string;
}

export const loadStripeConfig = async (charityId: string): Promise<StripeConfig> => {
  try {
    const database = firebase.database();

    const [accountTypeSnapshot, globalConfigSnapshot] = await Promise.all([
      database.ref(`${charityId}/config/stripe/accountType`).once('value'),
      database.ref('global/config/stripe').once('value'),
    ]);

    const accountType = accountTypeSnapshot.val() || "";
    const globalConfig = globalConfigSnapshot.val() || {};

    return {
      accountType,
      stripeClientId: globalConfig.clientId || "",
      redirectUrl: globalConfig.oauthRedirectUrl || "",
    };
  } catch (error) {
    console.error("Error loading Stripe config:", error);
    throw error;
  }
};
